let _ = t => t,
  _t;
import { Component, useMemo } from 'react';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { List } from 'foundations-components/transitional/components/List';
import { ResultsMessage } from 'foundations-components/transitional/components/ResultsMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { TeamOptionType } from '../types/OptionTypes';
import { ListsIcon } from 'foundations-assets/icons/components/ListsIcon';
import { css } from 'foundations-theming/utils';
import { getSortedFlattenedTeamsList } from '../utils/TeamUtils';
import styled from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const IndentWrapper = styled.div.withConfig({
  displayName: "SelectableTeamsList__IndentWrapper"
})(["", ""], ({
  theme,
  indentDepth
}) => css(_t || (_t = _`
    padding-inline-start: calc(${0} * ${0});
  `), theme.space['500'], indentDepth));
const TeamCheckbox = ({
  id,
  name,
  depth,
  checked,
  onRemoveTeam,
  onAddTeam,
  disabled
}) => {
  const isMac = navigator.userAgent.includes('Mac');
  const tooltipText = useMemo(() => {
    if (isMac) {
      return 'Click to select this Team and all its descendants. \n\nHold ⌘ and click to select this Team only.'; // Mac command symbol
    } else {
      return 'Click to select this Team and all its descendants. \n\nHold Ctrl and click to select this Team only.'; // Windows/Linux
    }
  }, [isMac]);
  return /*#__PURE__*/_jsx(IndentWrapper, {
    indentDepth: depth,
    children: /*#__PURE__*/_jsx(View, {
      as: "div",
      children: /*#__PURE__*/_jsx(Tooltip, {
        title: tooltipText,
        placement: "top",
        delay: 2000,
        delayOnHold: 700,
        textWrap: "pretty"
        // @ts-expect-error - valid style, applies correctly, types flag it incorrectly
        ,
        innerCssStyles: {
          whiteSpace: 'pre-wrap'
        },
        children: /*#__PURE__*/_jsx(Checkbox, {
          value: id,
          checked: checked,
          paddingInlineEnd: "100",
          inlineSize: "fit-content",
          disabled: disabled,
          "data-test-id": "team-checkbox",
          onChange: e => {
            if (checked) {
              onRemoveTeam(id);
            } else {
              if (isMac && e.nativeEvent.metaKey) {
                onAddTeam(id, false);
              } else if (!isMac && e.nativeEvent.ctrlKey) {
                onAddTeam(id, false);
              } else {
                onAddTeam(id, true);
              }
            }
          },
          children: /*#__PURE__*/_jsxs(Flex, {
            "data-test-id": `assign-team-checkbox-${id}`,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "200",
            children: [/*#__PURE__*/_jsx(ListsIcon, {}), /*#__PURE__*/_jsx(Text, {
              children: name
            })]
          })
        })
      })
    })
  });
};
class SelectableTeamsList extends Component {
  constructor(...args) {
    super(...args);
    this.renderTeamCheckbox = ({
      depth,
      id,
      name
    }) => {
      const {
        selectedTeamIds = [],
        onAddTeam,
        onRemoveTeam,
        rootLevelTeamIds,
        userHasTeamAssignmentScope,
        allowedTeamIds,
        DisallowedTeamFormattedMessage
      } = this.props;
      const checked = selectedTeamIds.includes(id);
      const isRootLevelTeam = rootLevelTeamIds.includes(id);
      const isDisabled = isRootLevelTeam && checked && !userHasTeamAssignmentScope;
      const isTeamDisallowed = allowedTeamIds && !allowedTeamIds.includes(id);
      const key = `${id}-team-checkbox`;
      const checkbox = /*#__PURE__*/_jsx(TeamCheckbox, {
        id: id,
        name: name,
        depth: depth,
        checked: checked,
        disabled: isDisabled || isTeamDisallowed,
        onAddTeam: onAddTeam,
        onRemoveTeam: onRemoveTeam
      }, key);
      if (isDisabled || isTeamDisallowed) {
        return /*#__PURE__*/_jsx(Tooltip, {
          placement: "left",
          title: isTeamDisallowed ? /*#__PURE__*/_jsx(DisallowedTeamFormattedMessage, {
            teamId: id,
            teamName: name
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'ui-asset-management-lib.tooltips.disabledTeamTooltip'
          }),
          children: /*#__PURE__*/_jsx("span", {
            children: checkbox
          })
        }, key);
      }
      return checkbox;
    };
  }
  render() {
    const {
      teams
    } = this.props;
    const teamOptions = getSortedFlattenedTeamsList(teams);
    if (!teamOptions.length) {
      return /*#__PURE__*/_jsx(ResultsMessage, {
        illustration: "empty-state-charts",
        illustrationProps: {
          width: 187
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ui-asset-management-lib.labels.noTeams"
        })
      });
    }
    return /*#__PURE__*/_jsx(List, {
      children: teamOptions.map(team => this.renderTeamCheckbox({
        depth: team.depth || 0,
        id: team.id,
        name: team.name
      }))
    });
  }
}
SelectableTeamsList.propTypes = {
  teams: PropTypes.arrayOf(TeamOptionType).isRequired,
  selectedTeamIds: PropTypes.arrayOf(PropTypes.number),
  onAddTeam: PropTypes.func.isRequired,
  onRemoveTeam: PropTypes.func.isRequired,
  rootLevelTeamIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  userHasTeamAssignmentScope: PropTypes.bool.isRequired,
  allowedTeamIds: PropTypes.arrayOf(PropTypes.number),
  DisallowedTeamFormattedMessage: PropTypes.func
};
export default SelectableTeamsList;